import { ACTION_LOGIN, ACTION_SIGNUP, PageType } from 'utils/analytics/eventTypesConstants';
import { AnalyticsSearchContext } from 'hooks/search/useSearchHandlers';
import { Network } from 'components/OAuth';
import { PaymentMethod } from 'forms/payment-method/PaymentMethodForm';
import { RatingSource, RatingTrigger } from 'routes/business/PostVisitFeedbackRoute';
import { SearchNavEntityTypes } from 'utils/searchBar';
import { WaitlistNotificationType } from 'utils/typeGuards';
import { WaitlistRequestStatus } from 'routes/business/search/ExperienceSearchRoute';
import { consumer, core } from '@tock/coretypes';

// actions

export const ACTION_VIEW_PAGE = 'View page';
export const PURCHASE = 'Purchase';

// Login
export const ACTION_CREDENTIALS_EXPOSED_DIALOG_DISMISS =
  'Dismiss credentials exposed password reset dialog';
export const ACTION_CREDENTIALS_EXPOSED_DIALOG_CLICK_RESET =
  'Click reset password button on credentials exposed password reset dialog';

// Home
export const ACTION_CLICK_PLATTER_CARD = 'Click platter card';
export const ACTION_DISMISS_SHOP_ANNOUNCEMENT_BANNER = 'Dismiss shop announcement banner';
export const ACTION_NAVIGATE_TO_JOIN = 'Navigate to join site';
export const ACTION_NAVIGATE_TO_BLOG = 'Navigate to B2C blog site';
export const ACTION_DOWNLOAD_IOS_APP = 'Download iOS app';

// Nav
export const ACTION_CLICK_USE_TOCK_BUSINESS = 'Click use Tock at your business';

//Receipt
export const ACTION_CLICK_ADD_TO_CALENDAR = 'Open add reservation to calendar';
export const ACTION_CLICK_BUTTON_EXPORT_TO_GOOGLE_CALENDAR =
  'Export reservation to Google calendar';
export const ACTION_CLICK_BUTTON_EXPORT_TO_OUTLOOK_CALENDAR =
  'Export reservation to Outlook calendar';
export const ACTION_CLICK_BUTTON_EXPORT_ICAL_APPLE = 'Export reservation to Apple calendar';
export const ACTION_CLICK_LINK_EXPORT_ICAL = 'Export reservation as .ics file';
export const ACTION_CLICK_BUTTON_ADD_TICKETS_TO_APPLE_WALLET = 'Add event tickets to Apple Wallet';
export const ACTION_CANCEL_RESERVATION = 'Cancel reservation';
export const ACTION_START_CANCEL_RESERVATION = 'Start cancel reservation';
export const ACTION_START_TRANSFER_RESERVATION = 'Start transfer reservation';
export const ACTION_START_MODIFY_RESERVATION = 'Start modify reservation';
export const ACTION_START_CANCEL_TRANSFER_RESERVATION = 'Start cancel transfer reservation';

export const ACTION_CONFIRM_RESERVATION = 'Confirm reservation'; // serverside text response event

// Pre visit questions
export const ACTION_VIEW_QUESTIONS_PROMPT = 'View questions prompt';
export const ACTION_OPEN_QUESTIONS_FORM = 'Open pre-visit question form';
export const ACTION_CLOSE_QUESTIONS_FORM = 'Close pre-visit question form';
export const ACTION_SUBMIT_QUESTIONS_FORM = 'Submit pre-visit question responses';
export const ACTION_VIEW_QUESTION_ANSWERS = 'View question answers';

// Widgets

// Hey you! Don't pattern match off these names. We don't like `Click` as an action verb. Use `Select` or `View` or `Go to` or `Start` or whatever your user's intention really is
export const ACTION_CLICK_GIFT_CARD_AD = 'Click Tock gift card ad';
export const ACTION_CLICK_MARKETING_FEATURE_CARD = 'Click marketing feature card';
export const ACTION_CLICK_BUSINESS_CARD = 'Click business card';
export const ACTION_CLICK_BUSINESS_GROUP_CARD = 'Click business group card';
export const ACTION_SELECT_EXPERIENCE_CARD = 'Select experience card';
export const ACTION_CLICK_METRO_AREA_CARD = 'Click metro area card';
export const ACTION_CLICK_SHOP_HERO_CTA = 'Click shop hero CTA';
export const ACTION_CLICK_CARD_LINK = 'Click collection card widget';
export const ACTION_CLICK_AVATAR_CARD_LINK = 'Click avatar collection card widget';
export const ACTION_CLICK_BUTTON_ARRAY_BUTTON = 'Click button array widget button';
export const ACTION_CLICK_CAROUSEL_SEE_MORE = 'Click carousel "see more" link';
export const ACTION_SCROLL_ITEM_INTO_VIEW = 'Scroll widget item into view';

// Nav
export const ACTION_GO_TO_LOGIN = 'Go to login';
export const ACTION_GO_TO_SIGNUP = 'Go to signup';

// Search
export const ACTION_SEARCH_DATE_RANGE = 'Search with date range';
export const ACTION_OPEN_SEARCH_OR_RESERVE_MODAL = 'Open search/reserve modal'; // for experiment HomePageSearchTubeHero
export const ACTION_OPEN_OMNISEARCH = 'Open omnisearch modal';

// Search results
export const ACTION_SELECT_BUSINESS_LIST_RESULT = 'Select business list result';
export const ACTION_SEARCH_MAP_BOUNDS = 'Search map bounds';
export const ACTION_SELECT_MAP_PIN = 'Select map pin';
export const ACTION_SELECT_MAP_CARD = 'Select map card';
export const ACTION_SCROLL_EXPERIENCE_LIST = 'Scroll experience list';
export const ACTION_SHOW_FULL_MAP = 'Show full map';
export const ACTION_SEARCH_AVAILABILITY = 'Search availability';
export const ACTION_SELECT_BUSINESS_EXP_LIST_RESULT = 'Select business experience list result';

// Business page
export const ACTION_BUSINESS_SELECT_EXPERIENCE = 'Select experience';
export const ACTION_SEARCH_BUSINESS_CALENDAR = 'Search business calendar';
export const ACTION_SEARCH_EXPERIENCE_CALENDAR = 'Search experience calendar';
export const ACTION_SELECT_BOOKING_TIME = 'Select experience booking time';
export const ACTION_BUSINESS_VIEW_TOCK_MENU = 'View Tock menu';
export const ACTION_BUSINESS_VIEW_CALENDAR_DATE = 'View business calendar date';
export const ACTION_BUSINESS_SELECT_SEATING_AREA_FILTER = 'Select seating area filter';
export const ACTION_BUSINESS_JOIN_RESERVATION_WAITLIST = 'Join reservation waitlist';
export const ACTION_BUSINESS_SELECT_BOOKING_CARD_OPEN = 'Open booking card';
export const ACTION_BUSINESS_SELECT_BOOKING_CARD_CLOSE = 'Close booking card';
export const ACTION_BUSINESS_CLICK_RESERVATION_TITLE = 'Click Business reservation title';
export const ACTION_BUSINESS_CLICK_RESERVATION_CTA = 'Click Business reservation CTA';
export const ACTION_BUSINESS_CLICK_RESERVATION_SEARCH = 'Click Business reservation search';
export const ACTION_BUSINESS_CLICK_MOBILE_RESERVATION_SEARCH =
  'Click Business  mobile reservation search';
export const ACTION_BUSINESS_CLICK_WALKIN_WAITLIST = 'Click Business walk-in waitlist';
export const ACTION_BUSINESS_CLICK_WALKIN_WAITLIST_SUBMIT =
  'Click Business walk-in waitlist submit';
export const ACTION_BUSINESS_CLICK_CHECKOUT_REVIEW = 'Click Business checkout review';
export const ACTION_BUSINESS_CLICK_SEARCH_MODAL_BOOK = 'Click Business search modal book';
export const ACTION_BUSINESS_CLICK_CHECKOUT_COMPLETE_PURCHASE =
  'Click Business checkout complete purchase';
export const ACTION_BUSINESS_VIEW_PAGE = 'View business page';

// Event page
export const ACTION_EVENT_VIEW_PAGE = 'View event page';
export const ACTION_EVENT_CLICK_CHOOSE_DATE_BUTTON = 'Click event page choose date button';
export const ACTION_EVENT_CLICK_BOOKING_BUTTON = 'Click event page booking button';

// Checkout
export const ACTION_CHECKOUT_COMPLETE_PROFILE = 'Checkout Complete profile';
export const ACTION_CHECKOUT_SAVE_ADDONS = 'Checkout Save addons';
export const ACTION_CHECKOUT_CHANGE_PAYMENT_METHOD = 'Checkout change payment method';
export const ACTION_CHECKOUT_ADDONS_GO_TO_NEXT_SECTION = 'Checkout Go to next addon section';
export const ACTION_CHECKOUT_ADDONS_OPEN_ADDON = 'Checkout Open add-on';
export const ACTION_CHECKOUT_VIEW_PAYMENT_FIELDS = 'Render Stripe payment element during checkout';
export const ACTION_CHECKOUT_ADDONS_SOLD_OUT_BLOCKS_CHECKOUT =
  'Checkout is blocked because of sold-out required add-ons';

// Shop
export const ACTION_SHOP_ADD_TO_CART = 'Shop add to cart';
export const ACTION_SHOP_SEARCH_ITEMS = 'Shop search items';
export const ACTION_SHOP_VIEW_ITEM = 'Shop view item';
export const ACTION_SHOP_CLICK_AVAILABLE_EXPERIENCES = 'Shop click available experiences';
export const ACTION_SHOP_ORDER = 'Shop order';
export const ACTION_SHOP_OPEN_BRAND_STORY = 'Shop open brand story';
export const ACTION_SHOP_SCROLL_BRAND_IMAGES = 'Shop scroll brand images';
export const ACTION_SHOP_OPEN_CART = 'Shop open cart';
export const ACTION_SHOP_VIEW_FULL_CART = 'Shop view full cart';
export const ACTION_SHOP_PROCEED_TO_CHECKOUT = 'Shop click cart checkout';
export const ACTION_SHOP_CHECKOUT_IN_VIEW = 'Shop cart checkout in view';
export const ACTION_SHOP_SEARCH_FILTER_SHOW_MORE = 'Shop search filter show more';
export const ACTION_SHOP_SEARCH_FILTER_SHOW_LESS = 'Shop search filter show less';
export const ACTION_SHOP_VIEW_PALETTE = 'Shop view palette';
export const ACTION_SHOP_SEARCH_TOGGLE_RESULTS_LAYOUT = 'Shop search toggle results layout';
export const ACTION_SHOP_VIEW_BUSINESS_SEARCH = 'Shop view business search';
export const ACTION_SHOP_BRAND_FAMILY_SEARCH = 'Shop view family of brands items';
export const ACTION_SHOP_GO_TO_BRAND_SHOP = 'Shop view items from brand';

// Shop item details page
export const ACTION_SHOP_VIEW_BRAND = 'Shop view brand';

// Shop checkout - shipping address
export const ACTION_SHOP_CHECKOUT_SHIPPING_ADDRESS_NEXT =
  'Shop checkout shipping address click next';
export const ACTION_SHOP_CHECKOUT_SHIPPING_ADDRESS_NEW_CLICK =
  'Shop checkout click new shipping address';
export const ACTION_SHOP_CHECKOUT_SHIPPING_ADDRESS_NEW_ADD =
  'Shop checkout submit new shipping address';
export const ACTION_SHOP_CHECKOUT_SHIPPING_ADDRESS_EDIT_CLICK =
  'Shop checkout click edit shipping address';
export const ACTION_SHOP_CHECKOUT_SHIPPING_ADDRESS_EDIT_SUBMIT =
  'Shop checkout submit edit shipping address';
export const ACTION_SHOP_CHECKOUT_GIFT_NOTE_EDIT_SUBMIT = 'Shop checkout submit edit gift note';

// Shop checkout - payment method
export const ACTION_SHOP_CHECKOUT_PAYMENT_NEXT = 'Shop checkout payment method click next';
export const ACTION_SHOP_CHECKOUT_BILLING_ADDRESS_SHIPPING_ADDRESS_CLICK =
  'Shop checkout payment method click billing same as shipping address';

// Profile
export const ACTION_REVIEW_VISIT = 'Review visit';

// Waitlist
export const ACTION_JOIN_WAITLIST = 'Join waitlist';
export const ACTION_REMOVE_WAITLIST_ENTRY = 'Remove waitlist entry';

// SMS Subscriptions
export const ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_OPEN =
  'Sms patron subscribe confirmation modal opened';
export const ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_SUBSCRIBE =
  'Sms patron subscribe confirmation modal subscribe';
export const ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_CLOSE =
  'Sms patron subscribe confirmation modal closed';

export type ShopContext =
  | 'Brand page'
  | 'Product search results'
  | 'Product page'
  | 'Widget'
  | 'Product search results modal'
  | 'Brand page modal'
  | 'Widget modal'
  | 'Product search results filter'
  | 'Product search results sort'
  | 'Product omnisearch results'
  | 'Product palette results'
  | 'Order details page'
  | 'Order receipt page'
  | 'Cart'
  | 'Checkout shipping'
  | 'Business page'
  | 'Homepage announcement bottom banner';

type WidgetType =
  | 'Shop hero widget'
  | 'Carousel widget'
  | 'Avatar collection card link widget'
  | 'Collection card link widget'
  | 'Feature carousel widget'
  | 'Button array widget'
  | 'Marketing widget';

export const itemAvailabilityToBaseAmplitudeAttributes = (item: consumer.ShopItemAvailability) => {
  return {
    productName: item.shopItem?.item?.name || undefined,
    productPriceCents: item.shopItem?.item?.priceCents || undefined,
    productDisplayPriceCents: item.shopItem?.item?.displayPriceCents || undefined,
    itemId: item.shopItem?.id || undefined,
    brandName: item.shopBrand?.name || undefined,
    brandId: item.shopBrand?.id || undefined,
  };
};

// data object
// limit of 1000 properties
export interface EventCustomAttributes {
  businessName?: string;
  businessGroupName?: string;
  businessId?: number;
  businessGroupId?: number;
  businessType?: string;
  businessState?: string;
  businessPriceRange?: string;
  businessListIndex?: number;
  currencyCode?: string;
  metroArea?: string;
  searchContext?: AnalyticsSearchContext;
  listIndex?: number;
  offeringSearchType?: consumer.OfferingSearchType;
  title?: string;
  $revenue?: number; // special amplitude property
  resultCount?: number; // search results, widget results, etc.
  offeringType?:
    | (consumer.Offering.Type | 'PAYMENT_CARD_HOLD')
    | (consumer.Offering.Type | 'PAYMENT_CARD_HOLD')[];
  pageType?: PageType;
  isLoginRequired?: boolean;
  hasAddOns?: boolean;
  experienceImages?: boolean;
  acceptingReservations?: boolean;
  numExperiencesWithSeatingOptions?: number;
  customData?: { [k: string]: string | number | boolean };

  clickContext?: 'CTA Button' | 'Title' | 'Image';
  pageRegion?: 'Header' | 'Footer';

  // widgets
  pageWidgetListIndex?: number;
  widgetName?: string;
  widgetType?: WidgetType;
  destinationHref?: string;

  // login
  auth?: {
    authType?: typeof ACTION_LOGIN | typeof ACTION_SIGNUP;
    network?: Network;
  };

  // authorization
  sessionToken?: string;

  // nav
  navCategory?: SearchNavEntityTypes;
  navEntry?: string;
  query?: string;

  // shop product
  productName?: string;
  quantity?: number;
  productPriceCents?: number;
  productDisplayPriceCents?: number;
  itemId?: number;
  brandName?: string;
  brandId?: number;
  itemSearchFilters?: string[];
  itemSearchFilterName?: string;
  // These are relevant for, e.g., clicks across from one brand page to another
  sourceBrandName?: string;
  sourceBrandId?: number;

  // reservations checkout
  addOnId?: number;
  addOnName?: string;
  addOnListIndex?: number;
  addOnGroupId?: number;
  addOnGroupName?: string;
  addOnGroupListIndex?: number;
  addOnCheckoutDisplayMode?: 'Multi-step' | 'Compact';

  // purchase
  purchaseId?: number;
  // amountCents is consider PII so do not track
  reservationDateTime?: string;
  date?: string;
  time?: string;
  partySize?: number;
  purchaseKeyValue?: { [key: string]: string }[];
  seatingArea?: string;
  isOptedInBusinessMarketing?: boolean;

  // payment
  paymentMethod?: PaymentMethod;
  previousPaymentMethod?: PaymentMethod;
  availablePaymentMethods?: PaymentMethod[];
  isSavedCreditCard?: boolean;
  paymentProcessorType?: core.PaymentProcessorType;
  isSupportedApplePayEnvironment?: boolean;

  // experience
  experienceId?: number;
  experienceName?: string;
  experienceVariety?: string;
  hasMobileTicket?: boolean;
  seatingAreaOptions?: string[];

  // business search
  hasExactMatch?: boolean | 'NOT_APPLICABLE';

  // offerings search
  minDateTime?: string;
  maxDateTime?: string;
  minPartySize?: number;
  maxPartySize?: number;
  city?: string;
  dateRange?: string;
  seatingAreaId?: number;
  seatingAreaName?: string;

  // native sentiment prompt
  sentimentPromptResponse?: string;

  // profile
  ratingSource?: RatingSource;
  ratingTrigger?: RatingTrigger;
  rating?: number;
  hasFeedback?: boolean;

  // pre visit questions
  questionOrigin?: 'EMAIL' | 'NAVIGATION';
  questionDisplayConfig?: 'DEFAULT' | 'URGENT' | 'PROMPT_ON_CONFIRM';
  questionCount?: number;
  answerCount?: number;

  // waitlist
  waitlistSource?: string;
  // relevant for requests from the business offering search result
  waitlistStatus?: WaitlistRequestStatus;
  waitlistNearestAvailabilityMinutes?: number;
  waitlistNotificationType?: WaitlistNotificationType;
}
